import React, { useEffect, useRef, useState } from 'react';
import { shopApi } from '../utils/shopApi';
import { Button } from '../components/ui/button';
import { Bike, MapPin, Phone, Package, ChevronRight, CheckCircle2, Radio } from 'lucide-react';
import { toast } from 'sonner';

const formatRWF = (n) => new Intl.NumberFormat('en-RW', { maximumFractionDigits: 0 }).format(n);

const STAGE_LABELS = {
  confirmed: { label: 'Confirmed — to prepare', next: 'preparing', cta: 'Start preparing' },
  preparing: { label: 'Preparing', next: 'dispatched', cta: 'Mark dispatched' },
  dispatched: { label: 'Dispatched', next: 'en_route', cta: 'En route now' },
  en_route: { label: 'En route', next: 'delivered', cta: 'Mark delivered' },
  delivered: { label: 'Delivered — awaiting customer confirmation', next: null, cta: null },
};

export default function RiderDashboardPage() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tracking, setTracking] = useState(false);
  const [lastPing, setLastPing] = useState(null);
  const watchId = useRef(null);

  const load = () => {
    setLoading(true);
    shopApi.get(`/shop/rider/orders`).then(r => setOrders(r.data)).catch(() => toast.error('Failed to load orders')).finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 30000); // poll every 30s
    return () => {
      clearInterval(t);
      if (watchId.current !== null) navigator.geolocation.clearWatch(watchId.current);
    };
  }, []);

  const pushLocation = async (pos) => {
    try {
      await shopApi.post('/shop/rider/location', {
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
        accuracy: pos.coords.accuracy,
        heading: pos.coords.heading,
      });
      setLastPing(new Date());
    } catch { /* ignore single failures */ }
  };

  const toggleTracking = () => {
    if (tracking) {
      if (watchId.current !== null) navigator.geolocation.clearWatch(watchId.current);
      watchId.current = null;
      setTracking(false);
      toast.info('Location sharing stopped');
      return;
    }
    if (!navigator.geolocation) { toast.error('Geolocation not available on this device'); return; }
    watchId.current = navigator.geolocation.watchPosition(
      pushLocation,
      (err) => toast.error('Location error: ' + err.message),
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 },
    );
    setTracking(true);
    toast.success('Sharing live location with customers');
  };

  const advance = async (order) => {
    try {
      await shopApi.put(`/shop/rider/orders/${order.id}/advance`);
      toast.success(`Order #${order.order_number} → ${STAGE_LABELS[order.status]?.next?.replace(/_/g, ' ')}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed');
    }
  };

  const mapHref = (addr) => {
    if (addr?.lat && addr?.lng) return `https://www.google.com/maps/dir/?api=1&destination=${addr.lat},${addr.lng}`;
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent((addr?.line || '') + ', ' + (addr?.city || ''))}`;
  };

  // Group by stage for mobile clarity
  const byStage = orders.reduce((acc, o) => {
    (acc[o.status] = acc[o.status] || []).push(o);
    return acc;
  }, {});
  const stages = ['confirmed', 'preparing', 'dispatched', 'en_route', 'delivered'];

  return (
    <div className="space-y-5 max-w-2xl mx-auto md:mx-0">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-serif font-bold flex items-center gap-2"><Bike className="w-7 h-7 text-primary" />Rider</h1>
          <p className="text-sm text-muted-foreground">Your active deliveries · refreshes every 30s</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="rider-refresh">Refresh</Button>
      </div>

      {/* Live location sharing */}
      <div className={`p-3 rounded-xl border ${tracking ? 'bg-green-50 border-green-300' : 'bg-card border-border'}`} data-testid="rider-location-card">
        <div className="flex items-center gap-2">
          <Radio className={`w-5 h-5 ${tracking ? 'text-green-600 animate-pulse' : 'text-muted-foreground'}`} />
          <div className="flex-1 min-w-0">
            <div className="font-medium text-sm">{tracking ? 'Live location sharing — ON' : 'Live location sharing — OFF'}</div>
            <div className="text-xs text-muted-foreground">{tracking
              ? (lastPing ? `Last ping: ${lastPing.toLocaleTimeString()}` : 'Waiting for first GPS fix…')
              : 'Customers can track your position while you deliver.'}</div>
          </div>
          <Button size="sm" variant={tracking ? 'outline' : 'default'} onClick={toggleTracking} data-testid="rider-toggle-location">
            {tracking ? 'Stop' : 'Start sharing'}
          </Button>
        </div>
      </div>

      {loading && orders.length === 0 && <div className="text-muted-foreground text-sm">Loading…</div>}
      {!loading && orders.length === 0 && (
        <div className="p-8 text-center bg-card rounded-xl border border-border" data-testid="rider-empty">
          <Package className="w-10 h-10 mx-auto text-muted-foreground mb-2" />
          <p className="text-sm text-muted-foreground">No active deliveries right now.</p>
        </div>
      )}

      {stages.map(stage => {
        const list = byStage[stage] || [];
        if (list.length === 0) return null;
        return (
          <section key={stage} data-testid={`rider-stage-${stage}`}>
            <h2 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground mb-2">{STAGE_LABELS[stage]?.label || stage}</h2>
            <div className="space-y-2">
              {list.map(o => (
                <div key={o.id} className="bg-card rounded-xl border border-border p-4 shadow-sm" data-testid={`rider-order-${o.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="font-mono text-xs text-muted-foreground">Order #{o.order_number}</div>
                      <div className="font-semibold mt-0.5">{o.customer_name}</div>
                      <div className="text-xs text-muted-foreground">{o.items.length} item(s) · RWF {formatRWF(o.total)}</div>
                    </div>
                    <span className="px-2 py-0.5 rounded-full text-[10px] bg-primary/10 text-primary uppercase">{stage.replace(/_/g,' ')}</span>
                  </div>
                  <div className="mt-3 flex flex-col gap-1.5 text-sm">
                    <a href={mapHref(o.address)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-primary hover:underline" data-testid={`rider-map-${o.id}`}>
                      <MapPin className="w-4 h-4" />
                      <span className="line-clamp-1">{o.address?.line}, {o.address?.city}</span>
                    </a>
                    {o.customer_phone && (
                      <a href={`tel:${o.customer_phone}`} className="inline-flex items-center gap-1.5 text-foreground/80 hover:text-primary" data-testid={`rider-call-${o.id}`}>
                        <Phone className="w-4 h-4" />{o.customer_phone}
                      </a>
                    )}
                    {o.address?.notes && <div className="text-xs text-muted-foreground italic">📝 {o.address.notes}</div>}
                  </div>
                  {STAGE_LABELS[stage]?.next ? (
                    <Button className="w-full mt-3" onClick={() => advance(o)} data-testid={`rider-advance-${o.id}`}>
                      {STAGE_LABELS[stage].cta} <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  ) : (
                    <div className="mt-3 flex items-center gap-2 text-sm text-muted-foreground"><CheckCircle2 className="w-4 h-4 text-green-600" />Waiting for customer to confirm receipt</div>
                  )}
                </div>
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}
